import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { mapToServiceRequests } from '~/modules/resourcing/common/util/resourceRequestUtil';
import { ResourceRequestStatus } from '~/types';
import {
  ALLOCATION_REJECTED_RESOURCE_REQUESTS,
  invokeBulkMarkResourceRequestsAsAllocationRejected
} from '~/modules/resourcing/common/enhancers/requestStatusHandlers/withBulkMarkResourceRequestsAsAllocationRejected';
import {
  COMPLETE_RESOURCE_REQUESTS,
  invokeBulkMarkResourceRequestsAsComplete
} from '~/modules/resourcing/common/enhancers/requestStatusHandlers/withBulkMarkResourceRequestsAsComplete';
import {
  UNSUBMIT_RESOURCE_REQUESTS,
  invokeBulkMarkResourceRequestsAsDraft
} from '~/modules/resourcing/common/enhancers/requestStatusHandlers/withBulkMarkResourceRequestsAsDraft';
import {
  REJECTED_RESOURCE_REQUESTS,
  invokeBulkMarkResourceRequestsAsRejected
} from '~/modules/resourcing/common/enhancers/requestStatusHandlers/withBulkMarkResourceRequestsAsRejected';
import {
  invokeBulkMarkResourceRequestsAsSubmitted,
  SUBMIT_RESOURCE_REQUESTS
} from '~/modules/resourcing/common/enhancers/requestStatusHandlers/withBulkMarkResourceRequestsAsSubmitted';
import {
  TENTATIVE_RESOURCE_REQUESTS,
  invokeBulkMarkResourceRequestsAsTentative
} from '~/modules/resourcing/common/enhancers/requestStatusHandlers/withBulkMarkResourceRequestsAsTentative';
import {
  TOBEHIRED_RESOURCE_REQUESTS,
  invokeBulkMarkResourceRequestsAsToBeHired
} from '~/modules/resourcing/common/enhancers/requestStatusHandlers/withBulkMarkResourceRequestsAsToBeHired';

export const isEmptyAllocations = resourceAllocations =>
  !resourceAllocations.some(alloc => !alloc.user.userUri);

export default ({
  resourceRequests,
  chartSelectedTab,
  requestAccessLevel,
  project: { id } = {}
}) => {
  const [bulkMarkResourceRequestsAsRejected] = useMutation(
    REJECTED_RESOURCE_REQUESTS
  );
  const rejectRequests = resourceRequests.filter(
    req =>
      (req.requestStatus === ResourceRequestStatus.Submitted ||
        req.requestStatus === ResourceRequestStatus.Tobehired) &&
      req.resourceAllocations.length === 0
  );

  const onAllResourceRequestsReject = useCallback(() => {
    bulkMarkResourceRequestsAsRejected(
      mapToServiceRequests({
        requests: rejectRequests,
        responseType: 'bulkMarkResourceRequestsAsRejected2',
        requestStatus: ResourceRequestStatus.Rejected,
        responseTypeName: 'BulkMarkResourceRequestsAsRejectedResult',
        chartSelectedTab,
        requestAccessLevel
      })
    );
  }, [
    bulkMarkResourceRequestsAsRejected,
    rejectRequests,
    chartSelectedTab,
    requestAccessLevel
  ]);

  const [bulkMarkResourceRequestsAsSubmitted] = useMutation(
    SUBMIT_RESOURCE_REQUESTS
  );

  const recallRequests = resourceRequests.filter(
    c => c.requestStatus === ResourceRequestStatus.Tentative
  );

  const onAllResourceRequestsRecalled = useCallback(() => {
    bulkMarkResourceRequestsAsSubmitted(
      mapToServiceRequests({
        requests: recallRequests,
        responseType: 'bulkMarkResourceRequestsAsSubmitted2',
        requestStatus: ResourceRequestStatus.Submitted,
        responseTypeName: 'BulkMarkResourceRequestsAsSubmittedResult',
        chartSelectedTab,
        requestAccessLevel
      })
    );
  }, [
    bulkMarkResourceRequestsAsSubmitted,
    recallRequests,
    chartSelectedTab,
    requestAccessLevel
  ]);

  const [bulkMarkResourceRequestsAsTentative] = useMutation(
    TENTATIVE_RESOURCE_REQUESTS
  );

  const tenatativeRequests = resourceRequests.filter(
    req =>
      (req.requestStatus === ResourceRequestStatus.Submitted ||
        req.requestStatus === ResourceRequestStatus.Allocationrejected) &&
      req.resourceAllocations.length !== 0 &&
      req.resourceAllocations.map(a => a.user).length !== 0
  );

  const onAllResourceRequestsFulfilled = useCallback(() => {
    bulkMarkResourceRequestsAsTentative(
      mapToServiceRequests({
        requests: tenatativeRequests,
        responseType: 'bulkMarkResourceRequestsAsTentative2',
        responseTypeName: 'BulkMarkResourceRequestsAsTentativeResult',
        requestStatus: ResourceRequestStatus.Tentative,
        chartSelectedTab,
        requestAccessLevel
      })
    );
  }, [
    bulkMarkResourceRequestsAsTentative,
    tenatativeRequests,
    chartSelectedTab,
    requestAccessLevel
  ]);

  const toBeHiredRequests = resourceRequests.filter(
    req =>
      (req.requestStatus === ResourceRequestStatus.Submitted ||
        req.requestStatus === ResourceRequestStatus.Rejected) &&
      req.resourceAllocations.length === 0
  );

  const [bulkMarkResourceRequestsAsToBeHired] = useMutation(
    TOBEHIRED_RESOURCE_REQUESTS
  );

  const onAllResourceRequestsToBeHired = useCallback(() => {
    bulkMarkResourceRequestsAsToBeHired(
      mapToServiceRequests({
        requests: toBeHiredRequests,
        responseType: 'bulkMarkResourceRequestsAsToBeHired2',
        responseTypeName: 'BulkMarkResourceRequestsAsToBeHiredResult',
        requestStatus: ResourceRequestStatus.Tobehired,
        chartSelectedTab,
        requestAccessLevel
      })
    );
  }, [
    bulkMarkResourceRequestsAsToBeHired,
    toBeHiredRequests,
    chartSelectedTab,
    requestAccessLevel
  ]);

  const unSubmitRequests = resourceRequests.filter(
    c =>
      c.requestStatus === ResourceRequestStatus.Tobehired ||
      c.requestStatus === ResourceRequestStatus.Submitted ||
      c.requestStatus === ResourceRequestStatus.Allocationrejected
  );

  const [bulkMarkResourceRequestsAsDraft] = useMutation(
    UNSUBMIT_RESOURCE_REQUESTS
  );

  const onAllResourceRequestsToBeUnSubmitted = useCallback(() => {
    bulkMarkResourceRequestsAsDraft(
      mapToServiceRequests({
        requests: unSubmitRequests,
        responseType: 'bulkMarkResourceRequestsAsDraft2',
        responseTypeName: 'BulkMarkResourceRequestsAsDraftResult',
        requestStatus: ResourceRequestStatus.Draft,
        chartSelectedTab,
        requestAccessLevel
      })
    );
  }, [
    bulkMarkResourceRequestsAsDraft,
    unSubmitRequests,
    chartSelectedTab,
    requestAccessLevel
  ]);

  const completeOrAllocationRejectRequests = resourceRequests.filter(
    c => c.requestStatus === ResourceRequestStatus.Tentative
  );

  const [bulkMarkResourceRequestsAsComplete] = useMutation(
    COMPLETE_RESOURCE_REQUESTS
  );

  const onAllResourceRequestsToBeCompleted = useCallback(() => {
    bulkMarkResourceRequestsAsComplete(
      mapToServiceRequests({
        requests: completeOrAllocationRejectRequests,
        responseType: 'bulkMarkResourceRequestsAsComplete2',
        responseTypeName: 'BulkMarkResourceRequestsAsCompleteResult',
        requestStatus: ResourceRequestStatus.Complete,
        chartSelectedTab,
        requestAccessLevel
      })
    );
  }, [
    bulkMarkResourceRequestsAsComplete,
    completeOrAllocationRejectRequests,
    chartSelectedTab,
    requestAccessLevel
  ]);

  const [bulkMarkResourceRequestsAsAllocationRejected] = useMutation(
    ALLOCATION_REJECTED_RESOURCE_REQUESTS
  );

  const onAllResourceRequestsToBeAllocationRejected = useCallback(() => {
    bulkMarkResourceRequestsAsAllocationRejected(
      mapToServiceRequests({
        requests: completeOrAllocationRejectRequests,
        responseType: 'bulkMarkResourceRequestsAsAllocationRejected2',
        responseTypeName: 'BulkMarkResourceRequestsAsAllocationRejectedResult',
        requestStatus: ResourceRequestStatus.Allocationrejected,
        chartSelectedTab,
        requestAccessLevel
      })
    );
  }, [
    bulkMarkResourceRequestsAsAllocationRejected,
    completeOrAllocationRejectRequests,
    chartSelectedTab,
    requestAccessLevel
  ]);

  const submitRequests = resourceRequests.filter(
    c =>
      c.requestStatus === ResourceRequestStatus.Draft ||
      c.requestStatus === ResourceRequestStatus.Rejected
  );

  const onAllResourceRequestsToBeSubmitted = useCallback(() => {
    bulkMarkResourceRequestsAsSubmitted(
      mapToServiceRequests({
        requests: submitRequests,
        responseType: 'bulkMarkResourceRequestsAsAllocationRejected2',
        responseTypeName: 'BulkMarkResourceRequestsAsAllocationRejectedResult',
        requestStatus: ResourceRequestStatus.Allocationrejected,
        chartSelectedTab,
        requestAccessLevel
      })
    );
  }, [
    bulkMarkResourceRequestsAsSubmitted,
    submitRequests,
    chartSelectedTab,
    requestAccessLevel
  ]);

  const acceptRequests = resourceRequests.filter(
    c =>
      c.requestStatus === ResourceRequestStatus.Tentative ||
      (c.requestStatus === ResourceRequestStatus.Submitted &&
        c.resourceAllocations.length !== 0)
  );

  const onAllResourceRequestsToBeAccepted = useCallback(() => {
    bulkMarkResourceRequestsAsComplete(
      mapToServiceRequests({
        requests: acceptRequests,
        responseType: 'bulkMarkResourceRequestsAsAllocationRejected2',
        responseTypeName: 'BulkMarkResourceRequestsAsAllocationRejectedResult',
        requestStatus: ResourceRequestStatus.Allocationrejected,
        chartSelectedTab,
        requestAccessLevel
      })
    );
  }, [
    bulkMarkResourceRequestsAsComplete,
    acceptRequests,
    chartSelectedTab,
    requestAccessLevel
  ]);

  const onAllResourceRequestsSubmit = useCallback(() => {
    return invokeBulkMarkResourceRequestsAsSubmitted(
      bulkMarkResourceRequestsAsSubmitted,
      id
    )(
      resourceRequests.filter(
        c =>
          c.requestStatus === ResourceRequestStatus.Draft ||
          c.requestStatus === ResourceRequestStatus.Rejected
      )
    );
  }, [bulkMarkResourceRequestsAsSubmitted, id, resourceRequests]);

  const onAllResourceRequestsAccept = useCallback(
    () =>
      invokeBulkMarkResourceRequestsAsComplete(
        bulkMarkResourceRequestsAsComplete,
        id
      )(
        resourceRequests.filter(
          c =>
            c.requestStatus === ResourceRequestStatus.Tentative ||
            (c.requestStatus === ResourceRequestStatus.Submitted &&
              c.resourceAllocations.length !== 0)
        )
      ),
    [bulkMarkResourceRequestsAsComplete, id, resourceRequests]
  );

  const onAllResourceRequestsAllocationReject = useCallback(
    () =>
      invokeBulkMarkResourceRequestsAsAllocationRejected(
        bulkMarkResourceRequestsAsAllocationRejected,
        id
      )(
        resourceRequests.filter(
          req => req.requestStatus === ResourceRequestStatus.Tentative
        )
      ),
    [bulkMarkResourceRequestsAsAllocationRejected, id, resourceRequests]
  );

  const onAllResourceRequestsUnsubmit = useCallback(
    () =>
      invokeBulkMarkResourceRequestsAsDraft(
        bulkMarkResourceRequestsAsDraft,
        id
      )(
        resourceRequests.filter(
          c =>
            c.requestStatus === ResourceRequestStatus.Tobehired ||
            c.requestStatus === ResourceRequestStatus.Submitted ||
            c.requestStatus === ResourceRequestStatus.Allocationrejected
        )
      ),
    [bulkMarkResourceRequestsAsDraft, id, resourceRequests]
  );

  const onAllResourceRequestsRelease = useCallback(
    () =>
      invokeBulkMarkResourceRequestsAsSubmitted(
        bulkMarkResourceRequestsAsSubmitted,
        id
      )(
        resourceRequests.filter(
          c => c.requestStatus === ResourceRequestStatus.Complete
        )
      ),
    [bulkMarkResourceRequestsAsSubmitted, id, resourceRequests]
  );

  const onAllToBeHiredResourceRequestsToSubmit = useCallback(
    () =>
      invokeBulkMarkResourceRequestsAsSubmitted(
        bulkMarkResourceRequestsAsSubmitted,
        id
      )(
        resourceRequests.filter(
          c => c.requestStatus === ResourceRequestStatus.Tobehired
        )
      ),
    [bulkMarkResourceRequestsAsSubmitted, id, resourceRequests]
  );

  const onAllResourceRequestsFulfilledWithoutCache = useCallback(
    () =>
      invokeBulkMarkResourceRequestsAsTentative(
        bulkMarkResourceRequestsAsTentative,
        id
      )(
        resourceRequests.filter(
          req =>
            (req.requestStatus === ResourceRequestStatus.Submitted ||
              req.requestStatus === ResourceRequestStatus.Allocationrejected) &&
            req.resourceAllocations.length > 0 &&
            isEmptyAllocations(req.resourceAllocations)
        )
      ),
    [bulkMarkResourceRequestsAsTentative, id, resourceRequests]
  );

  const onAllResourceRequestsRejectWithoutCache = useCallback(
    () =>
      invokeBulkMarkResourceRequestsAsRejected(
        bulkMarkResourceRequestsAsRejected,
        id
      )(
        resourceRequests.filter(
          req =>
            (req.requestStatus === ResourceRequestStatus.Submitted ||
              req.requestStatus === ResourceRequestStatus.Tobehired) &&
            req.resourceAllocations.length === 0
        )
      ),
    [bulkMarkResourceRequestsAsRejected, id, resourceRequests]
  );

  const onAllResourceRequestsRecalledWithoutCache = useCallback(
    () =>
      invokeBulkMarkResourceRequestsAsSubmitted(
        bulkMarkResourceRequestsAsSubmitted,
        id
      )(
        resourceRequests.filter(
          c => c.requestStatus === ResourceRequestStatus.Tentative
        )
      ),
    [bulkMarkResourceRequestsAsSubmitted, id, resourceRequests]
  );

  const onAllResourceRequestsToBeHiredWithoutCache = useCallback(
    () =>
      invokeBulkMarkResourceRequestsAsToBeHired(
        bulkMarkResourceRequestsAsToBeHired,
        id
      )(
        resourceRequests.filter(
          req =>
            (req.requestStatus === ResourceRequestStatus.Draft ||
              req.requestStatus === ResourceRequestStatus.Submitted ||
              req.requestStatus === ResourceRequestStatus.Rejected) &&
            req.resourceAllocations.length === 0
        )
      ),
    [bulkMarkResourceRequestsAsToBeHired, id, resourceRequests]
  );

  return {
    onAllResourceRequestsToBeUnSubmitted,
    onAllResourceRequestsToBeCompleted,
    onAllResourceRequestsToBeAllocationRejected,
    onAllResourceRequestsToBeHired,
    onAllResourceRequestsReject,
    onAllResourceRequestsRecalled,
    onAllResourceRequestsFulfilled,
    onAllResourceRequestsToBeSubmitted,
    onAllResourceRequestsFulfilledWithoutCache,
    onAllResourceRequestsRejectWithoutCache,
    onAllResourceRequestsRecalledWithoutCache,
    onAllResourceRequestsToBeHiredWithoutCache,
    onAllResourceRequestsToBeAccepted,
    onAllResourceRequestsSubmit,
    onAllResourceRequestsAccept,
    onAllResourceRequestsAllocationReject,
    onAllResourceRequestsUnsubmit,
    onAllResourceRequestsRelease,
    onAllToBeHiredResourceRequestsToSubmit
  };
};
